import type { IEvent } from "@/core/plugins/Events/Event";
import type { AxiosInstance } from "axios";
import axios from "axios";
import { injectable } from "inversify";
import "reflect-metadata";
import type { IHttpTokenInterceptor } from "./middleware/HttpTokenInterceptor";
import {
  HttpClient,
  IHttpDeleteParams,
  IHttpGetParams,
  IHttpPatchParams,
  IHttpPostParams,
  IHttpPutParams,
  IHttpResponse
} from "./protocols/types";
import { handleSessionResponse, sessionRequestUrl } from "./middleware/SessionAuthentication";

@injectable()
export class AxiosAdapter implements HttpClient {
  private axios!: AxiosInstance;

  private requiresBearer = true;

  private handlesSession = true;

  private axiosTokenInterceptor?: IHttpTokenInterceptor<AxiosInstance>;

  constructor(
    _event: IEvent,
    axiosTokenInterceptor?: IHttpTokenInterceptor<AxiosInstance>
  ) {
    this.axiosTokenInterceptor = axiosTokenInterceptor;
  }

  setup(url: string, withCredentials = false, requiresBearer = true, handlesSession = true) {
    this.requiresBearer = requiresBearer;
    this.handlesSession = handlesSession;
    this.axios = axios.create({
      baseURL: url,
      withCredentials
    });

    this.handleToken();
    this.handleUnauthorized();
  }

  handleToken() {
    if (!this.handlesSession) return;
    this.axiosTokenInterceptor?.intercept(this.axios, this.requiresBearer);
  }

  private handleUnauthorized() {
    if (!this.handlesSession) return;
    this.axios.interceptors.response.use(async (response) => {
      await handleSessionResponse(response.status, response.config.method || "GET", sessionRequestUrl(response.config.url, response.config.baseURL), response.headers["x-location"]);

      return response;
    }, async (error) => {
      if (error.response) await handleSessionResponse(error.response.status, error.config.method || "GET", sessionRequestUrl(error.config.url, error.config.baseURL), error.response.headers?.["x-location"]);

      return Promise.reject(error);
    });
  }

  async patch<T>(params: IHttpPatchParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.patch(
      params.url,
      {
        ...params.body
      },
      params.config
    );

    return {
      statusCode: request.status,
      body: request.data
    };
  }

  async post<T>(params: IHttpPostParams): Promise<IHttpResponse<T>> {
    //const body = Array.isArray(params.body) ? params.body : { ...params.body };

    const request = await this.axios.post(
      params.url,
      params.body,
      params.config
    );

    return {
      statusCode: request.status,
      body: request.data,
      headers: request.headers
    };
  }

  async get<T>(params: IHttpGetParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.get(params.url, {
      params: params.queryParams,
      ...params.config
    });

    return {
      statusCode: request.status,
      body: request.data,
      headers: request.headers
    };
  }

  async delete<T>(params: IHttpDeleteParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.delete(params.url, {
      data: params.body,
      params: params.params
    });

    return {
      statusCode: request.status,
      body: request.data
    };
  }

  async put<T>(params: IHttpPutParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.put(params.url, {
      ...params.body
    });

    return {
      statusCode: request.status,
      body: request.data
    };
  }
}
